import { useState } from "react";
import { t } from "@/lib/i18n";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { readRef } from "@/lib/ref";
import { useAuth } from "@/App";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const packageId = params.get("package");
  const invitedEmail = params.get("email");
  const isInvited = params.get("invited") === "1";
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(invitedEmail || "");
  const [code, setCode] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      if (data.demo_otp) setDemoOtp(data.demo_otp);
      setStep("otp");
      toast.success(data.sent_via_email ? "OTP sent to your email" : "OTP generated (demo mode)");
    } catch (err) { toast.error(errDetail(err)); }
    setLoading(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ref = readRef();
      const { data } = await api.post("/auth/verify-otp", { email, code, ...(ref ? { ref_code: ref.code, ref_via: ref.via } : {}) });
      login(data.token, data.user);
      if (data.joined_team) toast.success(`Welcome! You've joined ${data.joined_team}.`);
      else toast.success(data.is_new ? "Welcome! Your demo mailbox is ready." : "Welcome back!");
      if (packageId && !data.joined_team) {
        localStorage.setItem("agi_pref_plan", packageId);
      } else {
        localStorage.removeItem("agi_pref_plan");
      }
      const stage = data.user.onboarding_stage || "done";
      navigate(stage !== "done" ? "/onboarding" : (packageId ? "/billing" : "/app"));
    } catch (err) { toast.error(errDetail(err)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
            <EnvelopeSimple size={20} weight="bold" className="text-white" />
          </div>
          <span className="font-heading font-bold text-xl text-gray-900">Agentic Inbox</span>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-8">
          {packageId && (
            <div data-testid="package-selected-banner" className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mb-4">
              {t("login.checkout_banner", "Sign in to continue to checkout for your selected plan.")}
            </div>
          )}
          {isInvited && (
            <div data-testid="invited-banner" className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mb-4">
              {t("login.invited_banner", "Verify your email to accept the team invitation.")}
            </div>
          )}
          {step === "email" ? (
            <form onSubmit={requestOtp}>
              <h1 className="font-heading text-2xl font-bold text-gray-900 mb-1">{t("login.signin_title", "Sign in")}</h1>
              <p className="text-sm text-gray-500 mb-6">{t("login.email_sub", "Enter your email — we'll send you a one-time code. New here? An account is created automatically.")}</p>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">{t("login.email_label", "Email address")}</label>
              <input data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4" />
              <button data-testid="request-otp-button" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 transition-colors text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-50">
                {loading ? t("login.sending", "Sending…") : t("login.continue_btn", "Continue with Email")}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp}>
              <h1 className="font-heading text-2xl font-bold text-gray-900 mb-1">{t("login.code_title", "Enter your code")}</h1>
              <p className="text-sm text-gray-500 mb-4">{t("login.code_sent", "We sent a 6-digit code to")} <span className="font-medium text-gray-800">{email}</span></p>
              {demoOtp && (
                <div data-testid="demo-otp-banner" className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <Sparkle size={18} className="text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <span className="font-semibold">{t("login.demo_mode", "Demo mode:")}</span> {t("login.demo_code_is", "your code is")}{" "}
                    <span data-testid="demo-otp-code" className="font-mono font-bold tracking-widest text-blue-700">{demoOtp}</span>
                    <div className="text-xs text-blue-600 mt-1">{t("login.demo_note", "In production, this is emailed via Brevo (configure in Admin Panel).")}</div>
                  </div>
                </div>
              )}
              <input data-testid="otp-code-input" required value={code} maxLength={6}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-center text-xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4" />
              <button data-testid="verify-otp-button" disabled={loading || code.length !== 6}
                className="w-full bg-blue-600 hover:bg-blue-700 transition-colors text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-50">
                {loading ? t("login.verifying", "Verifying…") : t("login.verify_btn", "Verify & Sign in")}
              </button>
              <button type="button" data-testid="back-to-email-button" onClick={() => { setStep("email"); setCode(""); }}
                className="w-full mt-2 text-sm text-gray-500 hover:text-gray-800 py-2">{t("login.diff_email", "Use a different email")}</button>
            </form>
          )}
        </div>
        <p className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-4">
          <ShieldCheck size={14} /> {t("login.secure_note", "Passwordless login secured with JWT")} · Super admin: admin@agenticinbox.com
        </p>
      </div>
    </div>
  );
}
